import * as React from "react";
import moment from "moment";
import AccuracyFilters from "@app/projects/network-assessment/accuracy/accuracyFilters";
import AccuracyResult from "@app/projects/network-assessment/accuracy/accuracyResult";
import AccuracyRegionSetIdByState from "@app/projects/network-assessment/accuracy/accuracyRegionSetIdByState";
import { AccuracyTrendChartItem, AdequacyTrendChartItem } from "@app/projects/results/common";
import { RegionSetQualifier } from "@app/region-sets";
import { NetworkAssessmentAnalysisType, ProjectRunConfigurationType, RegionSetStatusType, TimeFrequency, AttestationRateDays, ProjectPerspective } from "@app/projects/enumerators";
import RegionFilters from "@app/projects/regionFilters";
import { AdequacySharedProjectContextProps, RegionFilterItem } from "@app/projects/adequacySharedProjectContextProps";
import { SpecialtyGroupException } from "@app/exceptions/interfaces";
import { VirtualAssistantContext } from "@app/projects/network-assessment/provider-hub/interfaces";
import { Breadcrumb } from "@app/shared/breadcrumbs/breadcrumb";
import { AccuracyTemplate } from "@app/templates/accuracy/models";
import AdequacyResult from "@app/projects/network-assessment/adequacy/adequacyResult";
import ProjectOptions from "@app/projects/projectOptions";
import { ProjectRunConfigurationResult } from "@app/projects/projectRunConfigurationResult";

/** The shape of possible query string parameters. */
export interface AdequacyQueryShape {
  all?: string;
  exceptions?: string;
  gaps?: string;
  changedGaps?: string;
  viewBy?: ProjectPerspective;
}

interface CountyProjectContextProps extends AdequacySharedProjectContextProps {
  accuracyFilters: AccuracyFilters;
  accuracyResult: AccuracyResult;
  accuracyTrendChartData: AccuracyTrendChartItem[];
  adequacyResult: AdequacyResult;
  adequacyTrendChartData: AdequacyTrendChartItem[];
  adequacyTrendChartDataDualMembership: AdequacyTrendChartItem[];
  breadcrumbItems: (string | Breadcrumb)[];
  clearCachedImpactAnalysisRunConfigurations: () => void;
  clearFilters: () => void;
  cmsCreditIsApplying: boolean;
  cmsCreditShowOnlyChanges: boolean;
  cmsCreditTemplateValue: string;
  compareToAdequacyResult?: AdequacyResult;
  countyRegionSetIds: AccuracyRegionSetIdByState[];
  networkAssessmentAnalysisType: NetworkAssessmentAnalysisType;

  compareToAccuracyResult?: AccuracyResult;
  compareToRunConfiguration?: ProjectRunConfigurationResult;
  compareToRunConfigurationTimestamp?: moment.Moment;
  filters: RegionFilterItem[];
  forceUpdateProject: () => Promise<void>;
  hasProspectOrMarketProviders: boolean;
  hideProjectHeader: boolean;
  //getComparedViewByAllResults: (resultRegions: ResultRegion[]) => Promise<Array<ComparedRecord<AdequacyDetail>>>;
  loadedAccuracyTemplates: boolean;
  loadedFilters: boolean;
  loadingResults: boolean;

  regionFilters: RegionFilters;
  removeCountyFilter: (countyFilter: string) => void;
  removeFilter: (urlPart: string) => void;
  requeueMarketAndProspectNCP: () => Promise<void>;
  rerunAccuracyGapAnalysis: (projectId: number, projectRunConfigurationId: number) => Promise<void>;
  // this is redeclared here (in addition to the shared interface) because it's a RunConfigurationResult here
  runConfiguration: ProjectRunConfigurationResult;
  runConfigurationTimestamp: moment.Moment;
  runProject: () => Promise<void>;
  setAttestationRateDays: (attestationDays: AttestationRateDays) => void;
  setBreadcrumbItems: (newItems: (string | Breadcrumb)[]) => void;
  setBulkStatus: (regions: RegionSetQualifier[], newStatus: RegionSetStatusType) => void;
  setCMSCreditIsApplying: (active: boolean) => void;
  setCMSCreditShowOnlyChanges: (active: boolean) => void;
  setDualMembershipMode: (isDualMembershipMode: boolean, projectRunConfigurationType?: ProjectRunConfigurationType, primaryConfigurationId?: number, compareToRunConfigurationId?: number, forceLoading?: boolean, suppressCompanionCompare?: boolean) => void;
  setRunConfiguration: (runConfigurationId: number, runConfigurationTimestamp?: moment.Moment, isCompareTo?: boolean, runFrequency?: TimeFrequency, delayUpdate?: boolean) => Promise<Partial<CountyProjectContainerState>>;
  setException: (exception: SpecialtyGroupException) => Promise<void>;
  setHideProjectHeader: (shouldHide: boolean) => void;
  setLoading: (setLoading: boolean) => void | CountyProjectContainerState;
  setPerspective: (perspective: ProjectPerspective) => void;
  setProjectHasUnprocessedTerminations: (needsToProcess: boolean, returnPartial?: boolean) => void | Partial<ProjectOptions>;
  setProjectProjectionFlags: (hasUnprocessedProviderTerminations: boolean, hasUnprocessedTargetProviders: boolean, hasUnprocessedExcludedProviders?: boolean) => void;
  setProjectReadOnlyProperties: (isReadOnly: boolean, isEditRestricted: boolean, delay?: boolean) => ProjectOptions;
  setRegionFilters: (regionFilters: RegionFilters, isCancelled: () => boolean) => void;
  /**
   * Updates the virtual assistant context used by the Provider Hub Quinn panel.
   * The supplied context carries provider information and determines whether the
   * assistant panel is open or closed, consistent with the `virtualAssistantContext`
   * value exposed on this context.
   */
  setVirtualAssistantContext: (virtualAssistantContext: VirtualAssistantContext) => void;
  setStatus: (region: RegionSetQualifier, newStatus: RegionSetStatusType) => void;
  skipReloadDualMembershipFromLocation?: boolean;
  /**
   * Virtual assistant context consumed by the Provider Hub Quinn panel.
   * Carries provider metadata and UI state (open/closed) for the assistant.
   */
  virtualAssistantContext: VirtualAssistantContext;
  updateRunConfigurationState: (newState: Partial<CountyProjectContainerState>) => void;
  updateRunConfigurationComment: (comment: string, runConfigurationId: number) => void;
}

type CountyProjectContainerState = CountyProjectContextProps & {
  accuracyTemplate?: AccuracyTemplate;
};

const CountyProjectContext = React.createContext<CountyProjectContextProps>(null);

function withCountyProjectContext<T>(Component: React.ComponentType<React.PropsWithChildren<T & CountyProjectContextProps>>): React.ComponentType<React.PropsWithChildren<T>> {
  class WithCountyProjectContext extends React.PureComponent<T> {
    public render() {
      return (
        <CountyProjectContext.Consumer>
          {(contextProps) => <Component {...this.props} {...contextProps} />}
        </CountyProjectContext.Consumer>
      );
    }
  }

  return WithCountyProjectContext;
}

export { CountyProjectContainerState, CountyProjectContext, CountyProjectContextProps, withCountyProjectContext };
